// useUploadExtracted - fetch one upload's extraction view (status, summary,
// content warnings, cost, an email thread's attachments) for the Files page.
// ``staleTime`` is generous (5 min): the view changes only when the file is
// replaced or re-extracted.
import { useContext } from "react";
import { useQuery } from "react-query";
import { useAgentsHttp } from "../useAgentsHttp";
import { isExtractionInFlight } from "../../utils/extractionInFlight";
import { QK } from "../queryKeys";
import {
  UploadsExtractedContext,
  type UploadExtractedResult,
} from "./useUploadsExtracted";

export type ExtractionStatus = "pending" | "succeeded" | "failed" | "unsupported";
// One thing this file's extraction lost while still completing (a downscaled OCR
// page, an unanalysable embedded image, a vision render that stopped short). The
// extraction_status stays "succeeded" - the loss is carried here instead, so a
// complete-but-imperfect read is visible rather than reading as clean.
export interface ContentWarning {
  code: string;
  detail: string;
}

// One attachment on an email thread. An ingested attachment is an upload of its own,
// named by upload_id; a skipped one has none and carries the reason in note.
export interface ThreadAttachmentEntry {
  filename: string;
  mime_type: string | null;
  size_bytes: number | null;
  status: string;
  note: string | null;
  content_summary: string | null;
  upload_id?: string | null;
}

export interface ExtractionView {
  upload_id: string;
  kind: string | null;
  extraction_status: ExtractionStatus;
  extraction_error: string | null;
  extraction_version: string | null;
  extraction_attempted_at: string | null;
  // An email thread's attachment manifest (one entry per attachment), without the
  // attachments held back by the personal-mail check. Empty for any other file.
  thread_attachments: ThreadAttachmentEntry[];
  content_warnings: ContentWarning[];
  has_ai_trace: boolean;
  // Whether the extracted text was kept; the text is fetched lazily via
  // useUploadExtractedText when the operator opens it.
  has_extracted_text: boolean;
  // The one-line summary off the current content version - the same sentence the
  // agents' document inventory lists this file by. Null when extraction produced
  // none (an unreadable format, or a row that has not been read yet).
  summary: string | null;
  // USD cost of this file's extraction LLM pass(es), computed BE-side from the
  // token counts (never stored). null when no LLM call was made or the model
  // isn't priced. The FE only renders this - it never prices anything.
  cost_usd: number | null;
}

const QUERY_KEY = QK.uploadExtracted;

export const useUploadExtracted = (
  upload_id: string | null | undefined,
  options: { enabled?: boolean } = {},
): UploadExtractedResult => {
  const http = useAgentsHttp();
  // When rendered under an <UploadsExtractedProvider> that already covers this
  // upload, serve from the ONE batched query and disable the standalone
  // request - this is what collapses the Files page's per-row N+1 into a single
  // batched poll. Outside a provider (the expand-gated synced-files trees),
  // batch is null and the standalone query runs exactly as before.
  const batch = useContext(UploadsExtractedContext);
  const servedByBatch =
    batch != null && typeof upload_id === "string" && batch.owns(upload_id);

  const query = useQuery<ExtractionView>(
    [QUERY_KEY, upload_id ?? null],
    async () => {
      if (!upload_id) {
        throw new Error("upload_id is required");
      }
      return http<ExtractionView>("/uploads/extracted", { upload_id });
    },
    {
      enabled:
        Boolean(upload_id) && !servedByBatch && (options.enabled ?? true),
      refetchOnWindowFocus: false,
      staleTime: 5 * 60_000,
      // Extraction runs on the queue worker: a row is ``pending`` until a
      // session claims it, ``running`` while it extracts. Poll through both so
      // the panel updates on its own; stop at a terminal state.
      refetchInterval: (data) =>
        isExtractionInFlight(data?.extraction_status) ? 4000 : false,
    },
  );

  if (servedByBatch && batch != null && typeof upload_id === "string") {
    return batch.resultFor(upload_id);
  }
  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
  };
};
